import { resolvesToEmailField, resolvesToPhoneField } from "@/helpers/Validation";
import { FieldType } from "@/types/forms/FieldType";
import { FieldValidationType } from "@/types/forms/FieldValidationType";

interface PublicFormField {
	id?: string;
	name?: string;
	placeholder?: string;
	type?: FieldType;
	FieldValidators?: Array<{ type?: FieldValidationType } | null | undefined> | null;
}

export interface PublicFormInputAttributes {
	id: string;
	name: string;
	autocomplete: string;
}

interface AddressAutocompleteRule {
	pattern: RegExp;
	token: string;
	fieldTypes: readonly FieldType[];
}

const singleLineAddressFieldTypes = [FieldType.Text] as const;
const fullAddressFieldTypes = [FieldType.Text, FieldType.TextArea] as const;
const addressAutocompleteRules: AddressAutocompleteRule[] = [
	{ pattern: /^(?:address(?: line)? 1|street(?: line| address) 1)$/i, token: "address-line1", fieldTypes: singleLineAddressFieldTypes },
	{
		pattern:
			/^(?:address(?: line)? 2|street line 2|address (?:apt\.?|apartment|suite|unit)(?: number| no\.?)?|(?:apt\.?|apartment|suite)(?: number| no\.?)?)$/i,
		token: "address-line2",
		fieldTypes: singleLineAddressFieldTypes
	},
	{ pattern: /^(?:address(?: line)? 3|street line 3)$/i, token: "address-line3", fieldTypes: singleLineAddressFieldTypes },
	{ pattern: /^(?:address )?(?:city|town)$/i, token: "address-level2", fieldTypes: singleLineAddressFieldTypes },
	{
		pattern: /^(?:address (?:state|province|region)|state\s*(?:\/|or)\s*province|province\s*(?:\/|or)\s*state)$/i,
		token: "address-level1",
		fieldTypes: singleLineAddressFieldTypes
	},
	{ pattern: /^(?:zip(?: code)?|postal code)$/i, token: "postal-code", fieldTypes: singleLineAddressFieldTypes },
	{ pattern: /^address country$/i, token: "country-name", fieldTypes: singleLineAddressFieldTypes },
	{
		pattern: /^(?:(?:street|full|mailing|home|business) )?address$/i,
		token: "street-address",
		fieldTypes: fullAddressFieldTypes
	}
];

const contextualAddressAutocompleteRules: AddressAutocompleteRule[] = [
	{ pattern: /^(?:apt\.?|apartment|suite|unit)(?: number| no\.?)?$/i, token: "address-line2", fieldTypes: singleLineAddressFieldTypes },
	{ pattern: /^(?:state|province|region)$/i, token: "address-level1", fieldTypes: singleLineAddressFieldTypes },
	{ pattern: /^country$/i, token: "country-name", fieldTypes: singleLineAddressFieldTypes }
];

function fieldDescription(field: PublicFormField): string {
	return `${field.name ?? ""} ${field.placeholder ?? ""}`.trim();
}

function fieldLabel(field: PublicFormField): string {
	return (field.name?.trim() || field.placeholder?.trim() || "").replace(/\s+/g, " ");
}

function hasAddressContext(fields: readonly PublicFormField[]): boolean {
	const labels = fields.map(fieldLabel);
	return (
		labels.some((label) =>
			/^(?:address(?: line)? 1|street(?: line| address) 1|address|(?:street|full|mailing|home|business) address)$/i.test(label)
		) && labels.some((label) => /^(?:(?:address )?(?:city|town)|zip(?: code)?|postal code)$/i.test(label))
	);
}

function stableFieldKey(field: PublicFormField): string {
	if (field.id) return field.id;

	const nameKey = fieldDescription(field)
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/^-|-$/g, "");

	return nameKey || "unnamed";
}

export function getPublicFormAutocomplete(field: PublicFormField, fields: readonly PublicFormField[] = []): string {
	// The Address field type IS a full address control whatever the admin labelled it (LSP-19563),
	// so its token comes from the type and never from the label-matching rules below.
	if (field.type === FieldType.Address) return "street-address";

	const description = fieldDescription(field);
	if (/\b(first|given) name\b/i.test(description)) return "off";
	if (/\b(?:last|family|sur)\s*name\b/i.test(description)) return "off";
	if (resolvesToEmailField(field) || /\bemail\b/i.test(description)) return "off";
	if (resolvesToPhoneField(field) || /\b(phone|telephone|mobile)\b/i.test(description)) return "off";
	if (field.type === FieldType.Name) return "off";

	const label = fieldLabel(field);
	for (const rule of addressAutocompleteRules) {
		if (field.type && rule.fieldTypes.includes(field.type) && rule.pattern.test(label)) return rule.token;
	}
	if (hasAddressContext(fields)) {
		for (const rule of contextualAddressAutocompleteRules) {
			if (field.type && rule.fieldTypes.includes(field.type) && rule.pattern.test(label)) return rule.token;
		}
	}
	if (/\b(full )?name\b/i.test(description)) return "off";

	return "off";
}

/**
 * Browser autofill identifies controls by their stable ID/name pair and semantic token.
 * Both public form renderers use this helper so their generated controls cannot drift.
 */
export function getPublicFormInputAttributes(field: PublicFormField, fields: readonly PublicFormField[] = []): PublicFormInputAttributes {
	const fieldKey = stableFieldKey(field);

	return {
		id: `public-form-field-${fieldKey}`,
		name: fieldKey,
		autocomplete: getPublicFormAutocomplete(field, fields)
	};
}

/**
 * The V2 public form's honeypot wire name (LSP-19625).
 *
 * It used to be `__hp_email`, and "email" is exactly the token Chrome's autofill heuristics match
 * on. Chrome also ignores `autocomplete="off"` for email/address autofill, notably on Android — so
 * on a form with no visible email field the autofilled address had nowhere else to land and Chrome
 * filled the honeypot, making the API reject a legitimate submission with an opaque 422. The name
 * must stay opaque: no autofill token, nothing that reads as "honeypot" either.
 *
 * The API accepts this name and the legacy one, so a browser still running a cached bundle keeps
 * submitting successfully. Changing this value changes the wire contract with
 * apps/contact-api/src/controllers/forms.ts (HONEYPOT_FIELD_IDS).
 */
export const PUBLIC_FORM_HONEYPOT_FIELD_NAME = "__ls_vf9";

/**
 * Attributes that keep the honeypot out of browser autofill and password managers.
 * `new-password` is the value browsers actually honour for suppression; `off` is not.
 * Pair these with the `tabindex="-1"` / `aria-hidden="true"` the field already carries — those keep
 * it out of the accessibility tree and the tab order and must stay.
 */
export const publicFormHoneypotAttributes = {
	name: PUBLIC_FORM_HONEYPOT_FIELD_NAME,
	autocomplete: "new-password",
	"data-lpignore": "true",
	"data-1p-ignore": ""
} as const;
